import { COMMAND_CENTER_ROOT } from './commandCenterRoutes'
import { useCallback, useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ApiError, isAuthError } from '../../api/client'
import { TaskDetail, getTask, mutateTask, humanizeError, TEAM_ROOT, type TeamTask, type TaskStatus } from './comms'

/**
 * V2 Team task detail — route wrapper.
 *   /square/command-center/team/tasks/:taskId
 *
 * Owns a single-task fetch + mutations. Every change persists (audited). No
 * fabricated task — honest loading/auth/error/not-found states.
 */
export default function SquareCodeTeamTaskRoute() {
  const { taskId } = useParams<{ taskId: string }>()
  const navigate = useNavigate()
  const [task, setTask] = useState<TeamTask | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [authRequired, setAuthRequired] = useState(false)

  const load = useCallback(async (signal?: AbortSignal) => {
    if (!taskId) { setLoading(false); return }
    setLoading(true); setError(null)
    try {
      const t = await getTask(taskId, signal)
      if (signal?.aborted) return
      setTask(t); setLoading(false); setAuthRequired(false)
    } catch (err) {
      if (signal?.aborted) return
      if (isAuthError(err)) { setAuthRequired(true); setTask(null) } else { setError(humanizeError(err as ApiError)); setTask(null) }
      setLoading(false)
    }
  }, [taskId])
  useEffect(() => { const ac = new AbortController(); load(ac.signal); return () => ac.abort() }, [load])

  const run = async (fn: () => Promise<unknown>) => { await fn(); await load() }

  return (
    <TaskDetail
      task={task}
      loading={loading}
      error={error}
      authRequired={authRequired}
      onStatus={(status: TaskStatus) => run(() => mutateTask('update_status', { id: taskId, status }))}
      onSetBlocker={(reason) => run(() => mutateTask('set_blocker', { id: taskId, blocker_reason: reason }))}
      onAssign={(assignee) => run(() => mutateTask('assign', { id: taskId, assignee_id: assignee }))}
      onHome={() => navigate(COMMAND_CENTER_ROOT)}
      onBackCommandCenter={() => navigate(COMMAND_CENTER_ROOT)}
      onBackTeam={() => navigate(TEAM_ROOT)}
      onNavTop={(to) => navigate(to)}
    />
  )
}
